import React from 'react'
import FieldError from 'Components/Element/FieldError'
import { Check } from 'lucide-react'

// Icons copied 1:1 from the design's "Additional information" screen
// (section12a) so the pixel shapes match exactly.
const PersonIcon = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? "#8a6231" : "rgba(20,33,61,.5)"} strokeWidth="1.7" className="flex-none">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </svg>
);

const TwoPeopleIcon = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? "#8a6231" : "rgba(20,33,61,.5)"} strokeWidth="1.7" className="flex-none">
    <circle cx="9" cy="8" r="3.2" />
    <circle cx="16" cy="9" r="3.2" />
    <path d="M3 21c0-3 2.5-5 6-5s6 2 6 5" />
  </svg>
);

const BuildingIcon = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? "#8a6231" : "rgba(20,33,61,.5)"} strokeWidth="1.7">
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M9 8h1M9 12h1M14 8h1M14 12h1M9 21v-4h6v4" />
  </svg>
);

function findField(sections, name) {
  for (const section of sections || []) {
    const match = section.fields?.find((f) => f.name === name);
    if (match) return match;
  }
  return null;
}

// The design's "Additional information" step (section12a) has a bespoke
// layout — two bordered question cards with icon-led choice rows side by
// side, then a narrower icon-tile pair below — rather than the generic
// pill/card select used elsewhere in the wizard. This mirrors it exactly
// while still reading label/required/error/options off the same
// formElements config (CaseFormFields.js, step 3) so validation and the
// Review page stay data-driven.
function AdditionalInfoFields({ field, formValues, onChangeUpdate, onBlurValidate, errors }) {
  const isPostedBySelfField = findField(field, "isPostedBySelf");
  const defendantField = findField(field, "defendant");
  const isEntityField = findField(field, "isEntity");
  const commonCauseField = findField(field, "classActionCommonCause");
  const arbitrationClauseField = findField(field, "arbitrationClause");

  const fireSelect = (fieldConfig, value) => {
    if (!fieldConfig) return;
    onChangeUpdate({ target: { value, name: fieldConfig.name } }, fieldConfig);
    onBlurValidate({ target: { value, name: fieldConfig.name } }, fieldConfig);
  };

  const postedBySelfValue = formValues?.[isPostedBySelfField?.name];
  const defendantValue = formValues?.[defendantField?.name];
  const entityValue = formValues?.[isEntityField?.name];
  const commonCauseValue = formValues?.[commonCauseField?.name];
  const arbitrationClauseValue = formValues?.[arbitrationClauseField?.name];

  // Compact 3-option pill row (Yes / No / Not sure) shared by both
  // class-action follow-up questions below - same visual language as the
  // "yes / no / I don't know" select fields elsewhere in the wizard
  // (RecursiveContainer.jsx's select case).
  const PillOptions = ({ fieldConfig, value }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {(fieldConfig.options || []).map((opt) => {
        const selected = String(value) === String(opt.id);
        return (
          <button
            type="button"
            key={String(opt.id)}
            onClick={() => fireSelect(fieldConfig, opt.id)}
            className={
              "flex items-center justify-center gap-1.5 text-center min-h-[52px] px-3.5 py-2.5 rounded-lg font-medium text-[13.5px] leading-snug transition-all " +
              (selected
                ? "border-2 border-tan bg-[rgba(138,98,49,.08)] text-tan shadow-[0_1px_4px_rgba(138,98,49,.15)]"
                : "border-[1.5px] border-[rgba(20,33,61,.15)] bg-ivory text-navy hover:border-[rgba(20,33,61,.3)] hover:bg-[rgba(20,33,61,.02)]")
            }
          >
            {selected && <Check size={14} strokeWidth={2.5} className="flex-none text-tan" />}
            <span>{opt.name}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="w-full font-plexsans">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 mb-8">
        {isPostedBySelfField && (
          <div className="p-6 border border-[rgba(20,33,61,.12)] rounded-[10px] bg-[rgba(20,33,61,.02)]">
            <label className="block font-medium text-[15px] text-navy mb-2.5">{isPostedBySelfField.label}</label>
            <div className="flex flex-col gap-2.5">
              {[
                { id: "yes", name: "It's happening to me", Icon: PersonIcon },
                { id: "no", name: "I'm helping a family member or friend", Icon: TwoPeopleIcon },
              ].map((opt) => {
                const selected = String(postedBySelfValue) === opt.id;
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => fireSelect(isPostedBySelfField, opt.id)}
                    className={
                      "flex items-center gap-2.5 p-3.5 rounded-lg text-left transition-colors " +
                      (selected
                        ? "border-2 border-tan bg-[rgba(138,98,49,.08)]"
                        : "border-[1.5px] border-[rgba(20,33,61,.15)] bg-ivory hover:border-[rgba(20,33,61,.3)]")
                    }
                  >
                    <opt.Icon active={selected} />
                    <span className="font-medium text-[14.5px] text-navy">{opt.name}</span>
                  </button>
                );
              })}
            </div>
            {errors?.[isPostedBySelfField.name] ? (
              <FieldError>{errors?.[isPostedBySelfField.name]}</FieldError>
            ) : ""}
          </div>
        )}

        {defendantField && (
          <div className="p-6 border border-[rgba(20,33,61,.12)] rounded-[10px] bg-[rgba(20,33,61,.02)]">
            <label className="block font-medium text-[15px] text-navy mb-2.5">{defendantField.label}</label>
            <div className="flex flex-col gap-2.5">
              {(defendantField.options || []).map((opt) => {
                const selected = String(defendantValue) === String(opt.id);
                return (
                  <button
                    type="button"
                    key={String(opt.id)}
                    onClick={() => fireSelect(defendantField, opt.id)}
                    className={
                      "text-left px-[15px] py-3.5 rounded-lg font-medium text-[14.5px] transition-colors " +
                      (selected
                        ? "border-2 border-tan bg-[rgba(138,98,49,.08)] text-tan"
                        : "border-[1.5px] border-[rgba(20,33,61,.15)] bg-ivory text-navy hover:border-[rgba(20,33,61,.3)]")
                    }
                  >
                    {opt.name}
                  </button>
                );
              })}
            </div>
            {errors?.[defendantField.name] ? (
              <FieldError>{errors?.[defendantField.name]}</FieldError>
            ) : ""}
          </div>
        )}
      </div>

      {/* Only asked when the user said they know of others in the same
          situation - these two answers are what actually distinguish a
          real class-action candidate from a merely similar individual
          claim (see CreateFormLanding.jsx's derivePartyDetails). */}
      {defendantValue === "yes" && (commonCauseField || arbitrationClauseField) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 mb-8">
          {commonCauseField && (
            <div className="p-6 border border-[rgba(20,33,61,.12)] rounded-[10px] bg-[rgba(20,33,61,.02)]">
              <label className="block font-medium text-[15px] text-navy mb-2.5">{commonCauseField.label}</label>
              <PillOptions fieldConfig={commonCauseField} value={commonCauseValue} />
              {errors?.[commonCauseField.name] ? (
                <FieldError>{errors?.[commonCauseField.name]}</FieldError>
              ) : ""}
            </div>
          )}

          {arbitrationClauseField && (
            <div className="p-6 border border-[rgba(20,33,61,.12)] rounded-[10px] bg-[rgba(20,33,61,.02)]">
              <label className="block font-medium text-[15px] text-navy mb-2.5">{arbitrationClauseField.label}</label>
              <PillOptions fieldConfig={arbitrationClauseField} value={arbitrationClauseValue} />
              {errors?.[arbitrationClauseField.name] ? (
                <FieldError>{errors?.[arbitrationClauseField.name]}</FieldError>
              ) : ""}
            </div>
          )}
        </div>
      )}

      {isEntityField && (
        <div className="max-w-[360px] mb-8">
          <label className="block font-medium text-[15px] text-navy mb-2.5">{isEntityField.label}</label>
          <div className="flex gap-2.5">
            {[
              { id: "yes", name: "Company", Icon: BuildingIcon },
              { id: "no", name: "Individual", Icon: PersonIcon },
            ].map((opt) => {
              const selected = String(entityValue) === opt.id;
              return (
                <button
                  type="button"
                  key={opt.id}
                  onClick={() => fireSelect(isEntityField, opt.id)}
                  className={
                    "flex-1 flex flex-col items-center gap-1.5 py-3.5 px-2 rounded-lg transition-colors " +
                    (selected
                      ? "border-2 border-tan bg-[rgba(138,98,49,.08)]"
                      : "border-[1.5px] border-[rgba(20,33,61,.15)] hover:border-[rgba(20,33,61,.3)]")
                  }
                >
                  <opt.Icon active={selected} />
                  <span className={"font-medium text-[13.5px] " + (selected ? "text-tan" : "text-[rgba(20,33,61,.55)]")}>{opt.name}</span>
                </button>
              );
            })}
          </div>
          {errors?.[isEntityField.name] ? (
            <FieldError>{errors?.[isEntityField.name]}</FieldError>
          ) : ""}
        </div>
      )}
    </div>
  );
}

export default AdditionalInfoFields
